import { useState, useCallback } from "react";

/**
 * Hook che incapsula la logica di drag-and-drop per riordinare una lista di
 * item identificati da un id. Usato in DeliveryRoundDetail per riordinare
 * le consegne ma riutilizzabile altrove.
 *
 * Restituisce gli handler da applicare ai container draggabili e lo stato
 * visivo (drag-over highlight) per ogni id.
 *
 * Uso:
 *   const { handleDragStart, handleDragOver, ... } = useReorderDragDrop(items, setItems)
 *   <div onDragStart={handleDragStart(id)} onDragOver={handleDragOver(id)} ...>
 */
export function useReorderDragDrop(items, setItems) {
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDragStart = (id) => (e) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch (err) {
      // Safari / older browsers may throw; drag still works via state.
      console.debug("[useReorderDragDrop] dataTransfer.setData failed (non-critical):", err?.message || err);
    }
  };

  const handleDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== id) setDragOverId(id);
  };

  const handleDragLeave = useCallback(() => setDragOverId(null), []);

  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    const sourceId = draggedId;
    setDraggedId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    setItems((prev) => {
      const next = [...prev];
      const sIdx = next.indexOf(sourceId);
      const tIdx = next.indexOf(targetId);
      if (sIdx < 0 || tIdx < 0) return prev;
      next.splice(sIdx, 1);
      const insertAt = next.indexOf(targetId);
      next.splice(insertAt, 0, sourceId);
      return next;
    });
  };

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  return {
    draggedId,
    dragOverId,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  };
}
